// canvas-visualiser.js — Shopify product page viewer — Elaramo Design
// Realistic gallery-wrap canvas render with fabric texture, corner folds, edge darkening

// ── Canvas weave texture generator
// Real canvas = tiny pores/holes at thread intersections, not stripes
function makeWeave(rU, rV) {
  const sz = 512;
  const c = document.createElement('canvas');
  c.width = c.height = sz;
  const x = c.getContext('2d');

  // Near-white linen base — keep it light so multiply blend preserves photo colours
  x.fillStyle = '#ece8e2';
  x.fillRect(0, 0, sz, sz);

  // Grid pitch: ~2px = tight canvas weave
  const pitch = 2;

  for (let row = 0; row < sz; row += pitch) {
    for (let col = 0; col < sz; col += pitch) {
      const ox = (Math.random() - 0.5) * 0.5;
      const oy = (Math.random() - 0.5) * 0.5;
      const cx = col + pitch / 2 + ox;
      const cy = row + pitch / 2 + oy;

      const r = 0.28 + Math.random() * 0.18;
      const alpha = 0.14 + Math.random() * 0.10;

      x.beginPath();
      x.arc(cx, cy, r, 0, Math.PI * 2);
      x.fillStyle = `rgba(80,68,52,${alpha})`;
      x.fill();

      if (Math.random() > 0.5) {
        x.beginPath();
        x.arc(cx + 0.3, cy - 0.3, r * 0.4, 0, Math.PI * 2);
        x.fillStyle = `rgba(255,250,242,${alpha * 0.35})`;
        x.fill();
      }
    }
  }

  // Light grain on top for cloth irregularity
  for (let i = 0; i < 25000; i++) {
    const a = Math.random() * 0.018;
    x.fillStyle = Math.random() > 0.5
      ? `rgba(50,40,28,${a})`
      : `rgba(240,232,220,${a * 0.7})`;
    x.fillRect(Math.random() * sz, Math.random() * sz, 1, 1);
  }

  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(rU || 3, rV || 2);
  t.encoding = THREE.sRGBEncoding;
  t._sourceCanvas = c;
  return t;
}

// ── Bump map: dot grid matching the pore pattern for surface relief
function makeBumpCanvas(sz) {
  const c = document.createElement('canvas');
  c.width = c.height = sz;
  const x = c.getContext('2d');
  x.fillStyle = '#909090'; // slightly above mid = slight overall raise
  x.fillRect(0, 0, sz, sz);
  const pitch = 2;
  for (let row = 0; row < sz; row += pitch) {
    for (let col = 0; col < sz; col += pitch) {
      const cx = col + pitch / 2 + (Math.random() - 0.5) * 0.4;
      const cy = row + pitch / 2 + (Math.random() - 0.5) * 0.4;
      x.beginPath();
      x.arc(cx, cy, 0.45, 0, Math.PI * 2);
      x.fillStyle = 'rgba(0,0,0,0.50)';
      x.fill();
    }
  }
  return c;
}

// ── Core mesh builder
function buildCanvasMesh(W, H, D, photoTex, weaveTexture) {
  const group = new THREE.Group();

  const PX = 512;
  const fW = Math.max(1, Math.round(W * PX));
  const fH = Math.max(1, Math.round(H * PX));
  const fD = Math.max(8, Math.round(D * PX));

  // Atlas layout (horizontal strip):
  // [FRONT fW×fH][LEFT fD×fH][RIGHT fD×fH][TOP fW×fD][BOTTOM fW×fD][BACK fW×fH]
  const AW = fW * 3 + fD * 2;
  const AH = fH;

  const atlas = document.createElement('canvas');
  atlas.width = AW;
  atlas.height = AH;
  const ac = atlas.getContext('2d');

  const C = {
    front:  { x: 0,                   y: 0, w: fW, h: fH },
    left:   { x: fW,                  y: 0, w: fD, h: fH },
    right:  { x: fW + fD,             y: 0, w: fD, h: fH },
    top:    { x: fW + fD * 2,         y: 0, w: fW, h: fD },
    bottom: { x: fW + fD * 2 + fW,    y: 0, w: fW, h: fD },
    back:   { x: fW + fD * 2 + fW * 2, y: 0, w: fW, h: fH },
  };

  // Source UV fractions for gallery-wrap slices
  const TW = W + 2 * D, TH = H + 2 * D;
  const u0 = D / TW, u1 = (D + W) / TW;
  const v0 = D / TH, v1 = (D + H) / TH;

  const img = photoTex && photoTex.image;
  const iw = img ? (img.naturalWidth  || img.width  || 1) : 1;
  const ih = img ? (img.naturalHeight || img.height || 1) : 1;

  // ── Draw a photo slice into a cell (pre-flip for Three.js flipY)
  function drawPhoto(cell, sx, sy, sw, sh, fx, fy) {
    ac.save();
    ac.translate(cell.x, cell.y + cell.h);
    ac.scale(1, -1);
    if (fx) { ac.translate(cell.w, 0); ac.scale(-1, 1); }
    if (fy) { ac.translate(0, cell.h); ac.scale(1, -1); }
    if (img) {
      ac.drawImage(img, sx * iw, sy * ih, sw * iw, sh * ih, 0, 0, cell.w, cell.h);
    } else {
      ac.fillStyle = '#d8d3cb';
      ac.fillRect(0, 0, cell.w, cell.h);
    }
    ac.restore();
  }

  // ── Overlay weave texture on a cell (multiply blend = fabric-over-ink look)
  function overlayWeave(cell, alpha) {
    const wc = weaveTexture && weaveTexture._sourceCanvas;
    if (!wc) return;
    // Create tiled pattern at cell size
    const tmp = document.createElement('canvas');
    tmp.width = cell.w; tmp.height = cell.h;
    const tc = tmp.getContext('2d');
    const pattern = tc.createPattern(wc, 'repeat');
    tc.fillStyle = pattern;
    tc.scale(cell.w / 256, cell.h / 256); // scale weave density per face size
    tc.fillRect(0, 0, 256, 256);

    ac.save();
    ac.translate(cell.x, cell.y + cell.h);
    ac.scale(1, -1);
    ac.globalCompositeOperation = 'multiply';
    ac.globalAlpha = alpha;
    ac.drawImage(tmp, 0, 0);
    ac.restore();
    ac.globalCompositeOperation = 'source-over';
    ac.globalAlpha = 1;
  }

  // ── Darken edges of a cell (simulates wrap-around shadow)
  function darkenEdges(cell, strength, sides) {
    // sides: array of 'top','bottom','left','right'
    ac.save();
    const spread = Math.min(cell.w, cell.h) * 0.45;
    sides.forEach(side => {
      let grad;
      if (side === 'top') {
        grad = ac.createLinearGradient(cell.x, cell.y, cell.x, cell.y + spread);
        grad.addColorStop(0, `rgba(0,0,0,${strength})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ac.fillStyle = grad;
        ac.fillRect(cell.x, cell.y, cell.w, spread);
      } else if (side === 'bottom') {
        grad = ac.createLinearGradient(cell.x, cell.y + cell.h, cell.x, cell.y + cell.h - spread);
        grad.addColorStop(0, `rgba(0,0,0,${strength})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ac.fillStyle = grad;
        ac.fillRect(cell.x, cell.y + cell.h - spread, cell.w, spread);
      } else if (side === 'left') {
        grad = ac.createLinearGradient(cell.x, cell.y, cell.x + spread, cell.y);
        grad.addColorStop(0, `rgba(0,0,0,${strength})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ac.fillStyle = grad;
        ac.fillRect(cell.x, cell.y, spread, cell.h);
      } else if (side === 'right') {
        grad = ac.createLinearGradient(cell.x + cell.w, cell.y, cell.x + cell.w - spread, cell.y);
        grad.addColorStop(0, `rgba(0,0,0,${strength})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ac.fillStyle = grad;
        ac.fillRect(cell.x + cell.w - spread, cell.y, spread, cell.h);
      }
    });
    ac.restore();
  }

  // ── Draw corner fold marks on side/top/bottom faces
  // A gallery wrap has a neat mitered fold at each corner
  function drawCornerFold(cell, corners) {
    // corners: array of 'tl','tr','bl','br'
    ac.save();
    const s = Math.min(cell.w, cell.h) * 0.55; // fold triangle size
    corners.forEach(corner => {
      let cx, cy, pts;
      if (corner === 'tl') {
        cx = cell.x; cy = cell.y;
        pts = [[cx, cy], [cx + s, cy], [cx, cy + s]];
      } else if (corner === 'tr') {
        cx = cell.x + cell.w; cy = cell.y;
        pts = [[cx, cy], [cx - s, cy], [cx, cy + s]];
      } else if (corner === 'bl') {
        cx = cell.x; cy = cell.y + cell.h;
        pts = [[cx, cy], [cx + s, cy], [cx, cy - s]];
      } else if (corner === 'br') {
        cx = cell.x + cell.w; cy = cell.y + cell.h;
        pts = [[cx, cy], [cx - s, cy], [cx, cy - s]];
      }
      if (!pts) return;

      // Soft fold shadow — very subtle, just a hint of depth at the corner
      ac.beginPath();
      ac.moveTo(pts[0][0], pts[0][1]);
      ac.lineTo(pts[1][0], pts[1][1]);
      ac.lineTo(pts[2][0], pts[2][1]);
      ac.closePath();
      const grad = ac.createRadialGradient(pts[0][0], pts[0][1], 0, pts[0][0], pts[0][1], s * 0.7);
      grad.addColorStop(0, 'rgba(0,0,0,0.10)');
      grad.addColorStop(0.6, 'rgba(0,0,0,0.04)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ac.fillStyle = grad;
      ac.fill();

      // Fold crease line — hairline only
      ac.beginPath();
      ac.moveTo(pts[1][0], pts[1][1]);
      ac.lineTo(pts[2][0], pts[2][1]);
      ac.strokeStyle = 'rgba(0,0,0,0.10)';
      ac.lineWidth = 0.8;
      ac.stroke();
    });
    ac.restore();
  }

  // ── BACK face: weave + stretcher bars
  {
    const cell = C.back;
    ac.save();
    ac.translate(cell.x, cell.y + cell.h);
    ac.scale(1, -1);
    const wc = weaveTexture && weaveTexture._sourceCanvas;
    if (wc) {
      const pat = ac.createPattern(wc, 'repeat');
      ac.fillStyle = pat;
      ac.fillRect(0, 0, cell.w, cell.h);
    } else {
      ac.fillStyle = '#e0d9ce';
      ac.fillRect(0, 0, cell.w, cell.h);
    }
    // Stretcher bars
    const bw = cell.w, bh = cell.h;
    const bt = Math.max(5, Math.round(bh * 0.045));
    ac.fillStyle = 'rgba(175,162,145,0.92)';
    ac.fillRect(0, 0, bw, bt);
    ac.fillRect(0, bh - bt, bw, bt);
    ac.fillRect(0, 0, bt, bh);
    ac.fillRect(bw - bt, 0, bt, bh);
    ac.fillRect(bw / 2 - bt / 2, 0, bt, bh);
    ac.fillRect(0, bh / 2 - bt / 2, bw, bt);
    ac.restore();
  }

  // ── FRONT face
  // Elaramo: "mirror" = de foto is alleen de voorkant, zijkanten zijn een spiegelbeeld van de rand
  const MIR = window._cvEdge !== 'wrap';
  const SOL = window._cvEdge === 'white' ? '#F4F2EE' : window._cvEdge === 'black' ? '#17191D' : null;
  function sideFill(cell) { ac.save(); ac.fillStyle = SOL; ac.fillRect(cell.x, cell.y, cell.w, cell.h); ac.restore(); }
  const mu = Math.min(0.5, D / W), mv = Math.min(0.5, D / H);
  if (MIR) drawPhoto(C.front, 0, 0, 1, 1);
  else drawPhoto(C.front, u0, v0, u1 - u0, v1 - v0);
  overlayWeave(C.front, 0.07); // barely-there weave on print surface
  // Very slight edge vignette on front
  darkenEdges(C.front, 0.07, ['top', 'bottom', 'left', 'right']);

  // ── SIDE faces: gallery wrap with darkening + corner folds
  // LEFT face
  if (SOL) sideFill(C.left); else if (MIR) drawPhoto(C.left, 0, 0, mu, 1, true, false);
  else drawPhoto(C.left, 0, v0, u0, v1 - v0);
  overlayWeave(C.left, 0.18);
  darkenEdges(C.left, 0.12, ['top', 'bottom']);
  darkenEdges(C.left, 0.06, ['right']);

  // RIGHT face
  if (SOL) sideFill(C.right); else if (MIR) drawPhoto(C.right, 1 - mu, 0, mu, 1, true, false);
  else drawPhoto(C.right, u1, v0, 1 - u1, v1 - v0);
  overlayWeave(C.right, 0.18);
  darkenEdges(C.right, 0.12, ['top', 'bottom']);
  darkenEdges(C.right, 0.06, ['left']);

  // TOP face
  if (SOL) sideFill(C.top); else if (MIR) drawPhoto(C.top, 0, 0, 1, mv, false, true);
  else drawPhoto(C.top, u0, 0, u1 - u0, v0);
  overlayWeave(C.top, 0.18);
  darkenEdges(C.top, 0.12, ['left', 'right']);
  darkenEdges(C.top, 0.06, ['bottom']);

  // BOTTOM face
  if (SOL) sideFill(C.bottom); else if (MIR) drawPhoto(C.bottom, 0, 1 - mv, 1, mv, false, true);
  else drawPhoto(C.bottom, u0, v1, u1 - u0, 1 - v1);
  overlayWeave(C.bottom, 0.18);
  darkenEdges(C.bottom, 0.12, ['left', 'right']);
  darkenEdges(C.bottom, 0.06, ['top']);

  // ── Build atlas texture
  const tex = new THREE.CanvasTexture(atlas);
  tex.encoding = THREE.sRGBEncoding;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;

  // ── Bump map for surface relief
  const bumpSz = 512;
  const bumpC = makeBumpCanvas(bumpSz);
  const bumpTex = new THREE.CanvasTexture(bumpC);
  bumpTex.wrapS = bumpTex.wrapT = THREE.RepeatWrapping;
  bumpTex.repeat.set(W * 12, H * 12); // fine weave repeat across mesh
  bumpTex.needsUpdate = true;

  // ── UV mapping
  function UV(cell, targetH) {
    const h = targetH !== undefined ? targetH : cell.h;
    return {
      u0: cell.x / AW,
      u1: (cell.x + cell.w) / AW,
      v0: 1 - (cell.y + h) / AH,
      v1: 1 - cell.y / AH,
    };
  }

  const geo = new THREE.BoxGeometry(W, H, D, 1, 1, 1);
  const ua = Float32Array.from(geo.attributes.uv.array);

  function setFace(i, u0, v0, u1, v1) {
    const b = i * 8;
    ua[b + 0] = u0; ua[b + 1] = v0;
    ua[b + 2] = u1; ua[b + 3] = v0;
    ua[b + 4] = u0; ua[b + 5] = v1;
    ua[b + 6] = u1; ua[b + 7] = v1;
  }

  const Ff = UV(C.front);
  const Fl = UV(C.left);
  const Fr = UV(C.right);
  const Ft = UV(C.top,    C.top.h);
  const Fb = UV(C.bottom, C.bottom.h);
  const Fk = UV(C.back);

  // BoxGeometry: 0=+X(right), 1=-X(left), 2=+Y(top), 3=-Y(bottom), 4=+Z(front), 5=-Z(back)
  setFace(0, Fr.u0, Fr.v0, Fr.u1, Fr.v1);
  setFace(1, Fl.u0, Fl.v0, Fl.u1, Fl.v1);
  setFace(2, Ft.u0, Ft.v0, Ft.u1, Ft.v1);
  setFace(3, Fb.u0, Fb.v0, Fb.u1, Fb.v1);
  setFace(4, Ff.u0, Ff.v0, Ff.u1, Ff.v1);
  setFace(5, Fk.u0, Fk.v0, Fk.u1, Fk.v1);

  geo.setAttribute('uv', new THREE.BufferAttribute(ua, 2));

  // ── Material: photo atlas + bump map for fabric surface relief
  const mat = new THREE.MeshStandardMaterial({
    map:      tex,
    bumpMap:  bumpTex,
    bumpScale: 0.008,
    roughness: 0.88,
    metalness: 0,
  });

  const box = new THREE.Mesh(geo, mat);
  group.add(box);

  return group;
}

// ── Preset views
const VIEWS = [
  { id: 'front',  rx:  0.05,              ry:  0             },
  { id: 'left',   rx:  0.05,              ry:  Math.PI / 2 - 0.1 },
  { id: 'right',  rx:  0.05,              ry: -Math.PI / 2 + 0.1 },
  { id: 'top',    rx: -Math.PI / 3 + 0.05, ry:  0            },
  { id: 'bottom', rx:  Math.PI / 3 - 0.05, ry:  0            },
];

const MAX_RY = Math.PI / 2 - 0.05;
const MAX_RX = Math.PI / 3;
function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }

let renderer, scene, camera, canvasGroup, weaveTexture, photoTex, animId;
let rotX = 0.05, rotY = 0, targetRotX = 0.05, targetRotY = 0;
let isDragging = false, lastMX = 0, lastMY = 0;

function switchView(viewId) {
  const v = VIEWS.find(v => v.id === viewId);
  if (!v) return;
  targetRotX = clamp(v.rx, -MAX_RX, MAX_RX);
  targetRotY = clamp(v.ry, -MAX_RY, MAX_RY);
  document.querySelectorAll('.cv-tab').forEach(t => {
    t.classList.toggle('cv-active', t.dataset.view === viewId);
  });
}

function getParams() {
  if (window._cvOpts) {
    const sz    = window._cvOpts.getSize  ? window._cvOpts.getSize()  : [60, 40];
    const depth = window._cvOpts.getDepth ? window._cvOpts.getDepth() : 2;
    return { cw: sz[0], ch: sz[1], depth };
  }
  return { cw: 60, ch: 40, depth: 2 };
}

function buildScene() {
  const stage = document.getElementById('cv-stage');
  if (!stage) return;
  /* Als stage geen afmetingen heeft, wacht op volgende frame */
  if (!stage.clientWidth || !stage.clientHeight) {
    requestAnimationFrame(() => requestAnimationFrame(buildScene));
    return;
  }
  /* Als renderer al bestaat, alleen resizen — niet opnieuw bouwen */
  if (renderer) {
    resizeRenderer();
    return;
  }
  renderer = new THREE.WebGLRenderer({
    canvas: document.getElementById('cv-gl'),
    antialias: true,
    alpha: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputEncoding  = THREE.sRGBEncoding;
  // lineair en neutraal: de kleuren op de voorkant komen zo vrijwel exact overeen met de foto
  renderer.toneMapping = THREE.LinearToneMapping;
  renderer.toneMappingExposure = 1.0; window._cvRenderer = renderer;
  renderer.physicallyCorrectLights = true;
  renderer.shadowMap.enabled = false;

  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xF2EFE9);
  window._cvScene     = scene;
  window._cvTargetBg  = new THREE.Color(0xF2EFE9);
  window._cvCurrentBg = new THREE.Color(0xF2EFE9);

  // ── External photo update API
  window._cvSetPhoto = function(dataUrl) {
    const img = new Image();
    img.onload = function() {
      if (photoTex) photoTex.dispose();
      photoTex = new THREE.Texture(img);
      photoTex.encoding        = THREE.sRGBEncoding;
      photoTex.wrapS           = photoTex.wrapT = THREE.ClampToEdgeWrapping;
      photoTex.generateMipmaps = true;
      photoTex.minFilter       = THREE.LinearMipmapLinearFilter;
      photoTex.magFilter       = THREE.LinearFilter;
      photoTex.needsUpdate     = true;
      rebuildCanvas();
    };
    img.src = dataUrl;
  };

  camera = new THREE.PerspectiveCamera(34, 1, 0.01, 50);
  camera.position.set(0, 0.1, 3.2);
  camera.lookAt(0, 0, 0);

  // Lighting: warm key from top-right, cool fill from left, soft ambient
  scene.add(new THREE.HemisphereLight(0xfff8f0, 0xe0d8d0, 1.6));

  const key = new THREE.DirectionalLight(0xfff5e8, 2.8);
  key.position.set(3.5, 6, 5);
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xe8f0ff, 0.75);
  fill.position.set(-5, 1.5, 3);
  scene.add(fill);

  // Subtle rim from below for canvas depth
  const rim = new THREE.DirectionalLight(0xffffff, 0.35);
  rim.position.set(0, -4, 2);
  scene.add(rim); window._cvScene = scene;
  // Lichtrichting (knop in de configurator): key + fill draaien mee, een zacht strijklicht geeft een zichtbaar verloop op de voorkant
  const glow = new THREE.PointLight(0xffeedd, 0, 0, 2);
  glow.position.set(0.95, 0.7, 0.85); scene.add(glow);
  const LP = { tl: [-1, 1], tr: [1, 1], br: [1, -1], bl: [-1, -1], c: [0, 0] };
  const tKey = key.position.clone(), tFill = fill.position.clone(), tGlow = glow.position.clone();
  let tGlowI = 0;
  window._cvSetLight = function (k) {
    if (k === 'off') { tKey.set(3.5, 6, 5); tFill.set(-5, 1.5, 3); tGlow.set(0.95, 0.7, 0.85); tGlowI = 0; return; }
    const p = LP[k]; if (!p) return;
    if (k === 'c') { tKey.set(0, 1.2, 6); tFill.set(0, -2, 4); } else { tKey.set(p[0] * 4, p[1] * 5, 4.5); tFill.set(-p[0] * 5, -p[1] * 1.2, 3); } tGlow.set(p[0] * 0.9, p[1] * 0.65, k === 'c' ? 1.05 : 0.75); tGlowI = k === 'c' ? 4.2 : 3.6;
  };
  window._cvLightTick = function () {
    key.position.lerp(tKey, 0.08); fill.position.lerp(tFill, 0.08); glow.position.lerp(tGlow, 0.08);
    glow.intensity += (tGlowI - glow.intensity) * 0.08;
  };

  weaveTexture = makeWeave(4, 3);
  resizeRenderer();
}

function resizeRenderer() {
  const stage = document.getElementById('cv-stage');
  if (!stage || !renderer) return;
  const w = stage.clientWidth, h = stage.clientHeight;
  if (!w || !h) return;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}
window.resizeRenderer = resizeRenderer;

function rebuildCanvas() {
  if (canvasGroup) {
    scene.remove(canvasGroup);
    canvasGroup.traverse(o => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) [].concat(o.material).forEach(m => {
        if (m.map   && m.map   !== photoTex) m.map.dispose();
        if (m.bumpMap) m.bumpMap.dispose();
        m.dispose();
      });
    });
  }
  const { cw, ch, depth } = getParams();
  const s = 1.6 / Math.max(cw, ch);
  const tri = window._cvOpts && window._cvOpts.getTri ? window._cvOpts.getTri() : null;
  if (tri && photoTex && photoTex.image) {
    // drieluik: drie losse panelen, elk met het eigen stuk van de foto (de randen lopen door uit de tussenruimte)
    canvasGroup = new THREE.Group();
    const img = photoTex.image, iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    const k = iw / (cw + 2 * depth);
    const cols = tri.cols || 3, rows = tri.rows || 1, ph = tri.ph || ch;
    for (let r = 0; r < rows; r++) for (let i = 0; i < cols; i++) {
      const c = document.createElement('canvas');
      const sx = i * (tri.pw + tri.gap) * k, sw = (tri.pw + 2 * depth) * k, sy = r * (ph + tri.gap) * k, sh = (ph + 2 * depth) * k;
      c.width = Math.max(1, Math.round(sw)); c.height = Math.max(1, Math.round(sh));
      try { c.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height); } catch (e) {}
      const g = buildCanvasMesh(tri.pw * s, ph * s, depth * s, { image: c }, weaveTexture);
      g.position.x = (i - (cols - 1) / 2) * (tri.pw + tri.gap) * s;
      g.position.y = ((rows - 1) / 2 - r) * (ph + tri.gap) * s;
      g.userData.to = { x: g.position.x, y: g.position.y };
      g.userData.from = { x: (i - (cols - 1) / 2) * tri.pw * s, y: ((rows - 1) / 2 - r) * ph * s };
      canvasGroup.add(g);
    }
  } else {
    canvasGroup = buildCanvasMesh(cw * s, ch * s, depth * s, photoTex, weaveTexture);
  }
  // wisselen tussen één canvas / twee- / drie- / vierluik: de panelen schuiven zacht uit elkaar
  const layKey = tri ? (tri.cols + 'x' + (tri.rows || 1)) : 'one';
  if (window._cvLayKey && window._cvLayKey !== layKey) canvasGroup.userData.split = { t: 0 };
  window._cvLayKey = layKey;
  if (canvasGroup.userData.split) cvSplitStep(canvasGroup);
  scene.add(canvasGroup);
}
function cvSplitStep(G) {
  const u = G.userData.split; if (!u) return;
  u.t = Math.min(1, u.t + 1 / 30);
  const e = 1 - Math.pow(1 - u.t, 3), sc = 0.94 + 0.06 * e;
  G.scale.set(sc, sc, sc);
  G.children.forEach(g => { const a = g.userData.from, b = g.userData.to; if (a && b) { g.position.x = a.x + (b.x - a.x) * e; g.position.y = a.y + (b.y - a.y) * e; } });
  if (u.t >= 1) G.userData.split = null;
}

// ── Auto-rotate state
let autoRotate     = false; // activates after reveal completes
let autoRotateTime = 0;     // elapsed ticks
const AUTO_ROT_SPEED = 0.0028; // radians per frame
const AUTO_ROT_PAUSE = 60;     // frames to wait after last interaction before resuming
let interactCooldown = 0;

function startRender() {
  if (animId) cancelAnimationFrame(animId);
  function loop() {
    animId = requestAnimationFrame(loop);

    // Auto-rotate: gentle Y swing (-35° .. +35°) using sine wave
    if (autoRotate && interactCooldown <= 0) {
      autoRotateTime += AUTO_ROT_SPEED;
      targetRotY = Math.sin(autoRotateTime) * 0.62;
      targetRotX = 0.05 + Math.sin(autoRotateTime * 0.4) * 0.06;
    }
    if (interactCooldown > 0) interactCooldown--;

    rotX += (targetRotX - rotX) * 0.10;
    rotY += (targetRotY - rotY) * 0.10;
    if (canvasGroup) {
      canvasGroup.rotation.x = rotX;
      canvasGroup.rotation.y = rotY;
    }
    tickReveal();
    if (canvasGroup && canvasGroup.userData.split) cvSplitStep(canvasGroup);
    if (window._cvFovUpdate) window._cvFovUpdate();
    if (window._cvCurrentBg && window._cvTargetBg && scene) {
      window._cvCurrentBg.lerp(window._cvTargetBg, 0.08);
      scene.background.copy(window._cvCurrentBg);
    }
    // alleen tekenen als de preview in beeld is (scheelt veel rekenkracht en batterij op telefoons)
    if (window._cvLightTick) window._cvLightTick();
    if (window._cvVis !== false) renderer.render(scene, camera);
  }
  loop();
}

function setupDrag() {
  const el = document.getElementById('cv-gl');
  if (!el) return;
  el.style.cursor = 'grab';

  // Tell the browser: this element handles its own touch gestures
  el.style.touchAction = 'none';

  // ── Mouse
  el.addEventListener('mousedown', e => {
    isDragging = true;
    lastMX = e.clientX; lastMY = e.clientY;
    el.style.cursor = 'grabbing';
    interactCooldown = 999999;
    const hint = document.getElementById('cv-drag-hint');
    if (hint) hint.style.opacity = '0';
  });
  window.addEventListener('mousemove', e => {
    if (!isDragging) return;
    const dx = e.clientX - lastMX, dy = e.clientY - lastMY;
    targetRotY = clamp(targetRotY + dx * 0.007, -MAX_RY, MAX_RY);
    targetRotX = clamp(targetRotX + dy * 0.007, -MAX_RX, MAX_RX);
    lastMX = e.clientX; lastMY = e.clientY;
  });
  window.addEventListener('mouseup', () => {
    isDragging = false;
    el.style.cursor = 'grab';
    interactCooldown = AUTO_ROT_PAUSE * 6;
  });

  // ── Touch — 1 vinger = draaien, 2 vingers = pinch-zoom
  let touchActive = false;
  let lastTX = 0, lastTY = 0;

  el.addEventListener('touchstart', e => {
    if (e.touches.length === 1) {
      // Single finger — start rotation
      touchActive = true;
      lastTX = e.touches[0].clientX;
      lastTY = e.touches[0].clientY;
      interactCooldown = 999999;
      const hint = document.getElementById('cv-drag-hint');
      if (hint) hint.style.opacity = '0';
    } else if (e.touches.length === 2) {
      // Two fingers — start pinch zoom, stop any rotation
      touchActive = false;
      if (window._cvPinchStart) window._cvPinchStart(e.touches);
      interactCooldown = 999999;
    }
  }, { passive: true });

  window.addEventListener('touchmove', e => {
    if (e.touches.length === 2) {
      // Pinch zoom — always prevent scroll for two fingers on canvas
      if (window._cvPinchMove) {
        e.preventDefault();
        window._cvPinchMove(e.touches);
      }
      return;
    }
    if (!touchActive) return;
    e.preventDefault(); // stops page scroll while rotating
    const dx = e.touches[0].clientX - lastTX;
    const dy = e.touches[0].clientY - lastTY;
    targetRotY = clamp(targetRotY + dx * 0.007, -MAX_RY, MAX_RY);
    targetRotX = clamp(targetRotX + dy * 0.007, -MAX_RX, MAX_RX);
    lastTX = e.touches[0].clientX;
    lastTY = e.touches[0].clientY;
  }, { passive: false });

  window.addEventListener('touchend', e => {
    if (e.touches.length < 2 && window._cvPinchEnd) window._cvPinchEnd();
    if (e.touches.length === 0) {
      touchActive = false;
      interactCooldown = AUTO_ROT_PAUSE * 6;
    } else if (e.touches.length === 1) {
      // One finger lifted from pinch — resume single-finger drag
      touchActive = true;
      lastTX = e.touches[0].clientX;
      lastTY = e.touches[0].clientY;
    }
  });

  window.addEventListener('touchcancel', () => {
    touchActive = false;
    if (window._cvPinchEnd) window._cvPinchEnd();
  });
}

function setupZoom() {
  const BASE_FOV = 34, MIN_FOV = BASE_FOV / 1.3, MAX_FOV = 65;
  let targetFov = BASE_FOV;

  window._cvFovUpdate = function () {
    if (!camera) return;
    const diff = targetFov - camera.fov;
    if (Math.abs(diff) > 0.05) {
      camera.fov += diff * 0.12;
      camera.updateProjectionMatrix();
    }
  };
  window._cvZoom = function (delta) {
    targetFov = Math.max(MIN_FOV, Math.min(MAX_FOV, targetFov + delta));
  };

  const stage = document.getElementById('cv-stage');
  const btnIn  = stage ? stage.querySelector('#cv-zoom-in')  : document.getElementById('cv-zoom-in');
  const btnOut = stage ? stage.querySelector('#cv-zoom-out') : document.getElementById('cv-zoom-out');
  if (btnIn)  btnIn.onclick  = e => { e.stopPropagation(); window._cvZoom(-2); };
  if (btnOut) btnOut.onclick = e => { e.stopPropagation(); window._cvZoom( 2); };

  // Desktop scroll wheel
  const el = document.getElementById('cv-gl');
  if (el) {
    el.addEventListener('wheel', e => {
      e.preventDefault();
      window._cvZoom(e.deltaY > 0 ? 1.5 : -1.5);
    }, { passive: false });
  }

  // ── Pinch-to-zoom (mobile two fingers)
  // Stored on window so setupDrag can coordinate
  let pinchActive   = false;
  let lastPinchDist = 0;

  function getPinchDist(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  window._cvPinchStart = function(touches) {
    pinchActive   = true;
    lastPinchDist = getPinchDist(touches);
  };

  window._cvPinchMove = function(touches) {
    if (!pinchActive) return;
    const dist = getPinchDist(touches);
    const delta = lastPinchDist - dist; // positive = fingers closer = zoom out
    window._cvZoom(delta * 0.08);
    lastPinchDist = dist;
  };

  window._cvPinchEnd = function() {
    pinchActive = false;
  };
}

// ── Reveal animation state
let revealProgress = 0; // 0..1
let revealActive   = false;

function startReveal() {
  // Elaramo: als de configurator de overgang zelf regelt, direct recht van voren tonen (geen eigen fade)
  if (window._cvNoReveal) {
    window._cvNoReveal = false;
    revealActive = false;
    rotX = targetRotX = 0.05; rotY = targetRotY = 0;
    autoRotateTime = 0; autoRotate = true; interactCooldown = 80;
    if (camera && typeof targetFov === "number" && Math.abs(camera.fov - targetFov) > 0.05) {
      camera.fov = targetFov; camera.updateProjectionMatrix();
    }
    if (canvasGroup) {
      canvasGroup.position.y = 0;
      canvasGroup.rotation.x = rotX; canvasGroup.rotation.y = rotY;
      canvasGroup.traverse(o => {
        if (o.material) { [].concat(o.material).forEach(m => { m.opacity = 1; m.transparent = false; }); }
      });
    }
    return;
  }
  revealProgress = 0;
  revealActive   = true;

  // Start canvas fully transparent and slightly below final position
  if (canvasGroup) {
    canvasGroup.material && (canvasGroup.material.transparent = true);
    canvasGroup.traverse(o => {
      if (o.material) {
        [].concat(o.material).forEach(m => {
          m.transparent = true;
          m.opacity = 0;
        });
      }
    });
    canvasGroup.position.y = -0.18;
  }
}

// Elaramo: schermpositie van de voorkant van het canvas (voor een naadloze overgang vanuit de upload)
window._cvProject = function () {
  if (!canvasGroup || !camera || !renderer || typeof THREE === "undefined") return null;
  canvasGroup.updateMatrixWorld(true);
  const b = new THREE.Box3().setFromObject(canvasGroup);
  if (b.isEmpty()) return null;
  const r = renderer.domElement.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  // rekenen met de uiteindelijke zoom (fov) en actuele camerastand
  const f0 = camera.fov;
  if (typeof targetFov === "number") { camera.fov = targetFov; }
  camera.aspect = r.width / r.height; camera.updateProjectionMatrix(); camera.updateMatrixWorld(true);
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  [[b.min.x, b.min.y], [b.max.x, b.min.y], [b.min.x, b.max.y], [b.max.x, b.max.y]].forEach(p => {
    const v = new THREE.Vector3(p[0], p[1], b.max.z).project(camera);
    const sx = r.left + (v.x + 1) / 2 * r.width, sy = r.top + (1 - v.y) / 2 * r.height;
    x0 = Math.min(x0, sx); x1 = Math.max(x1, sx); y0 = Math.min(y0, sy); y1 = Math.max(y1, sy);
  });
  if (camera.fov !== f0) { camera.fov = f0; camera.updateProjectionMatrix(); }
  return { left: x0, top: y0, width: x1 - x0, height: y1 - y0 };
};

function tickReveal() {
  if (!revealActive || !canvasGroup) return;

  // Ease out cubic
  revealProgress = Math.min(1, revealProgress + 0.018);
  const t = 1 - Math.pow(1 - revealProgress, 3);

  canvasGroup.traverse(o => {
    if (o.material) {
      [].concat(o.material).forEach(m => { m.opacity = t; });
    }
  });
  canvasGroup.position.y = -0.18 * (1 - t);

  if (revealProgress >= 1) {
    revealActive = false;
    canvasGroup.traverse(o => {
      if (o.material) {
        [].concat(o.material).forEach(m => {
          m.opacity = 1;
          m.transparent = false;
        });
      }
    });
    canvasGroup.position.y = 0;
    // Kick off auto-rotate, syncing sine phase to current rotation
    autoRotateTime = Math.asin(Math.max(-1, Math.min(1, targetRotY / 0.62)));
    autoRotate = true;
  }
}

// terug uit een kamer: canvas staat weer in de vaste schuine stand (zelfde kant als in de kamer), zonder te springen
window._cvResetAngle = function (ry, rx) {
  rotY = targetRotY = ry; rotX = targetRotX = (typeof rx === "number" ? rx : 0.05);
  autoRotateTime = Math.asin(Math.max(-1, Math.min(1, ry / 0.62)));
  interactCooldown = Math.max(interactCooldown, 120);
  if (canvasGroup) { canvasGroup.rotation.x = rotX; canvasGroup.rotation.y = rotY; }
};

// naar een kamer: canvas trekt zichzelf eerst recht, daarna volgt de overgang
window._cvStraighten = function (cb) {
  targetRotY = 0; targetRotX = 0; interactCooldown = Math.max(interactCooldown, 300);
  const t0 = performance.now();
  (function chk() {
    if ((Math.abs(rotY) < 0.008 && Math.abs(rotX) < 0.008) || performance.now() - t0 > 1500) { if (cb) cb(); return; }
    requestAnimationFrame(chk);
  })();
};

// ── Public API
window.initCanvasVisualiser = function (opts) {
  window._cvOpts = opts;

  if (renderer && opts && opts.imageUrl) {
    const imgU = new Image();
    imgU.onload = function() {
      if (photoTex) photoTex.dispose();
      photoTex = new THREE.Texture(imgU);
      photoTex.encoding = THREE.sRGBEncoding;
      photoTex.wrapS = photoTex.wrapT = THREE.ClampToEdgeWrapping;
      photoTex.generateMipmaps = true;
      photoTex.minFilter = THREE.LinearMipmapLinearFilter;
      photoTex.magFilter = THREE.LinearFilter;
      photoTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      photoTex.needsUpdate = true;
      rebuildCanvas();
      const cvLoad = document.getElementById('cv-loading');
      if (cvLoad) cvLoad.style.display = 'none';
      startReveal(); resizeRenderer();
    };
    imgU.src = opts.imageUrl;
    return;
  }

  if (typeof THREE === 'undefined') {
    if (!window._cvThreeLoading) {
      window._cvThreeLoading = true;
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
      s.onload = function() { window._cvThreeLoading = false; window.initCanvasVisualiser(window._cvOpts || opts); };
      s.onerror = function() {
        var s2 = document.createElement('script');
        s2.src = 'https://unpkg.com/three@0.128.0/build/three.min.js';
        s2.onload = function() { window._cvThreeLoading = false; window.initCanvasVisualiser(window._cvOpts || opts); };
        document.head.appendChild(s2);
      };
      document.head.appendChild(s);
    }
    return;
  }

  function doInit() {
    const stage = document.getElementById('cv-stage');
    if (!stage || !stage.clientWidth || !stage.clientHeight) {
      /* Stage nog niet zichtbaar — wacht via ResizeObserver */
      if (window.ResizeObserver) {
        const ro = new ResizeObserver((entries) => {
          for (const e of entries) {
            if (e.contentRect.width > 0 && e.contentRect.height > 0) {
              ro.disconnect();
              doInit();
              return;
            }
          }
        });
        ro.observe(stage || document.getElementById('cv-gl'));
      } else {
        setTimeout(doInit, 100);
      }
      return;
    }

    buildScene();
    startRender();
    setupDrag();
    setTimeout(setupZoom, 200);

    document.querySelectorAll('.cv-tab').forEach(t => {
      t.addEventListener('click', () => {
        switchView(t.dataset.view);
        interactCooldown = AUTO_ROT_PAUSE * 8;
      });
    });

    if (opts && opts.imageUrl) {
      const img = new Image();
      img.onload = function () {
        if (photoTex) photoTex.dispose();
        photoTex = new THREE.Texture(img);
        photoTex.encoding   = THREE.sRGBEncoding;
        photoTex.wrapS      = photoTex.wrapT = THREE.ClampToEdgeWrapping;
        photoTex.generateMipmaps = true;
        photoTex.minFilter  = THREE.LinearMipmapLinearFilter;
        photoTex.magFilter  = THREE.LinearFilter;
        photoTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
        photoTex.needsUpdate = true;
        rebuildCanvas();
        document.getElementById('cv-loading').style.display = 'none';
        startReveal();
        resizeRenderer();
        setTimeout(resizeRenderer, 100);
        setTimeout(resizeRenderer, 300);
      };
      img.onerror = () => { console.error('[CV] Foto laden mislukt:', opts.imageUrl ? opts.imageUrl.substring(0,80) : 'geen url'); document.getElementById('cv-loading').style.display = 'none'; };
      img.src = opts.imageUrl;
    } else {
      rebuildCanvas();
      document.getElementById('cv-loading').style.display = 'none';
    }

    window.addEventListener('resize', resizeRenderer);
  }

  doInit();

};

window.updateCanvasVisualiser = function () {
  if (!scene) return;
  rebuildCanvas();
};

/* Elaramo: 3D-preview pauzeren als hij buiten beeld is */
(function () {
  function watch() {
    var el = document.getElementById("cv-gl");
    if (!el || !("IntersectionObserver" in window)) return;
    window._cvVis = false;
    new IntersectionObserver(function (en) { window._cvVis = en[0].isIntersecting; }, { rootMargin: "200px 0px" }).observe(el);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", watch); else watch();
})();
